import { HiringGoalOverview } from 'api-clients/monolith/models/HiringGoalOverview';
import { PaginateJsonSchema } from 'api-clients/monolith/models/PaginateJsonSchema';
import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Applicant from '@/assets/icons/applicant.svg';
import IconRight from '@/assets/icons/icon-right.svg';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Typography } from '@/components/ui/typography';
import {
  GoalEndsLabel,
  GoalInstant,
  GoalStamp,
  GoalStartsLabel,
  GoalTimeZoneChip,
  GoalTimeZoneTooltipBody,
  getGoalWindow,
} from '@/components/pages/opening-details/overview/hiring-goal-view/goal-window';
import { useOpeningDetails } from '@/contexts/opening-context';
import { useHiringGoalIdParam } from '@/hooks/use-hiring-goal-id-param';
import { useLocalDateRanges } from '@/hooks/use-local-date-ranges';
import { cn } from '@/utils/classnames';
import { formatShortDate } from '@/utils/date-util';

import { HiringGoalPill } from './hiring-goal-pill';

interface HiringGoalDetailsListProps {
  hiringGoals: HiringGoalOverview[];
  goalStatus: string;
  pagination?: PaginateJsonSchema;
  setPage?: (page: number) => void;
  onClose: () => void;
}

function HiringGoalDetailsList({
  hiringGoals,
  goalStatus,
  pagination,
  setPage,
  onClose,
}: HiringGoalDetailsListProps) {
  const { hiringGoal, opening } = useOpeningDetails();
  const { setHiringGoalId } = useHiringGoalIdParam();
  const intl = useIntl();
  const localDays = useLocalDateRanges();
  const timeZone = opening.timeZone;
  // Created and Last updated read their day at the opening, as Starts does.
  const dayAtOpening = localDays ? { timeZone } : undefined;

  // Each goal's start and end at the opening. A goal long enough to span years scans
  // each day for a daylight-saving change, so the windows hold until the list changes.
  // A goal with an end but no end_at keeps the date-only duration.
  const goalWindows = React.useMemo(
    () =>
      new Map(
        hiringGoals.map(goal => [
          goal.external_id,
          localDays && (goal.end_at || !goal.hire_by)
            ? getGoalWindow(goal.start_date, goal.end_at, timeZone)
            : null,
        ]),
      ),
    [hiringGoals, localDays, timeZone],
  );
  const getProgress = (expected: number | null, actual: number | null) => {
    if (!actual || !expected) return 0;

    return (actual / expected) * 100;
  };

  const handleViewClick = (externalId: string) => {
    onClose();
    // `setHiringGoalId` changes `useOpeningDetails()`'s query key, which
    // resuspends `HiringGoalContent` — the same component that owns the
    // sheet's open state. Batched together, that suspend discards the
    // `onClose()` update too (it's the same render attempt), so the sheet
    // stays open until the new data resolves. Wrapping this call in a
    // transition decouples it: `onClose()` commits immediately, closing
    // the sheet, while the page content transitions in the background.
    React.startTransition(() => {
      setHiringGoalId(externalId);
    });
  };

  const noEndDate = (
    <FormattedMessage
      defaultMessage="No end date"
      description="Label for hiring goal with no end date"
    />
  );

  const handleLoadMore = (nextPage: number | null) => {
    if (setPage && nextPage !== null) {
      setPage(nextPage);
    }
  };

  return (
    <>
      {hiringGoals.map(goal => {
        const goalWindow = goalWindows.get(goal.external_id);
        return (
          <React.Fragment key={goal.external_id}>
            <div className="border-fx-neutral-200 flex shrink-0 flex-col gap-3 self-stretch overflow-hidden rounded-lg border bg-white">
              <div className="flex flex-col gap-3 px-3 pt-3">
                <div className="flex min-h-[36px] items-center gap-4 self-stretch">
                  <div className="flex grow items-center gap-2">
                    <Typography variant="large2">
                      <FormattedMessage
                        defaultMessage="{actual} of {expected}"
                        values={{
                          actual: goal.hire_stats.actual,
                          expected: goal.hire_stats.expected,
                        }}
                        description="Hires progress: {actual} done vs {expected} target for this goal"
                      />
                    </Typography>
                  </div>
                  <div className="flex w-[120px] justify-end">
                    {goalStatus !== 'scheduled' &&
                      (hiringGoal?.external_id !== goal.external_id ? (
                        <Button
                          variant="secondary"
                          onClick={() => handleViewClick(goal.external_id)}
                        >
                          <FormattedMessage
                            defaultMessage="View"
                            description="Button: open a different hiring goal in the list"
                          />
                          <img src={IconRight} alt="IconRight" />
                        </Button>
                      ) : (
                        <HiringGoalPill />
                      ))}{' '}
                  </div>
                </div>
                <Progress
                  value={getProgress(
                    goal.hire_stats.expected,
                    goal.hire_stats.actual,
                  )}
                  className="bg-fx-primary"
                />
              </div>
              <div className="border-fx-neutral-200 bg-fx-neutral-50 flex flex-col items-start gap-2 border-t p-3">
                <div className="flex items-start justify-between self-stretch">
                  <div
                    className={cn(
                      'flex flex-col items-start',
                      goalWindow ? 'gap-2' : 'gap-1',
                    )}
                  >
                    <Typography variant="caption">
                      <FormattedMessage
                        defaultMessage="Duration"
                        description="Label for hire goal start–end window on the card"
                      />
                    </Typography>
                    {goalWindow ? (
                      <div className="flex gap-3">
                        <GoalStamp label={<GoalStartsLabel />}>
                          <GoalInstant {...goalWindow.start} />
                        </GoalStamp>
                        <GoalStamp label={<GoalEndsLabel />}>
                          {goalWindow.end ? (
                            <GoalInstant {...goalWindow.end} />
                          ) : (
                            noEndDate
                          )}
                        </GoalStamp>
                      </div>
                    ) : (
                      <Typography variant="subtitle1">
                        {formatShortDate(goal.start_date)} -{' '}
                        {goal.hire_by
                          ? formatShortDate(goal.hire_by)
                          : noEndDate}
                      </Typography>
                    )}
                  </div>
                  <div className="flex flex-col items-start gap-1 self-stretch">
                    <Typography variant="caption">
                      <FormattedMessage
                        defaultMessage="Applicants"
                        description="Label for applicant count under that goal"
                      />
                    </Typography>
                    <div className="flex h-6 items-center justify-end gap-4">
                      <div className="flex h-4 items-center gap-2">
                        <img src={Applicant} alt="Applicant" />
                        <Typography variant="small1">
                          {goal.applied_count ?? 0}
                        </Typography>
                      </div>
                    </div>
                  </div>
                </div>
                {goalWindow && (
                  <div className="self-start">
                    <GoalTimeZoneChip
                      timeZone={timeZone}
                      timeZoneLabel={goalWindow.timeZoneLabel}
                      tooltipBody={
                        <GoalTimeZoneTooltipBody
                          goalWindow={goalWindow}
                          locationName={opening.locationName}
                        />
                      }
                    />
                  </div>
                )}
                {(goal.created_at || goal.updated_at) && (
                  <div className="flex w-full items-center justify-between">
                    {goal.created_at ? (
                      <Typography
                        variant="caption"
                        className="text-muted-foreground"
                      >
                        {intl.formatMessage(
                          {
                            defaultMessage: 'Created {date}',
                            description:
                              'Inline label + date for when this hiring goal was created',
                          },
                          {
                            date: intl.formatDate(
                              goal.created_at,
                              dayAtOpening,
                            ),
                          },
                        )}
                      </Typography>
                    ) : (
                      <span />
                    )}
                    {goal.updated_at && (
                      <Typography
                        variant="caption"
                        className="text-muted-foreground"
                      >
                        {intl.formatMessage(
                          {
                            defaultMessage: 'Last updated {date}',
                            description:
                              'Inline label + date for when this hiring goal was last updated',
                          },
                          {
                            date: intl.formatDate(
                              goal.updated_at,
                              dayAtOpening,
                            ),
                          },
                        )}
                      </Typography>
                    )}
                  </div>
                )}
              </div>
            </div>
          </React.Fragment>
        );
      })}

      {pagination && pagination.current_page < pagination.total_pages && (
        <div className="flex items-center justify-end gap-1 self-stretch p-1.5">
          <Button
            variant="ghost"
            onClick={() => handleLoadMore(pagination.next_page)}
          >
            <Typography variant="body2">
              <FormattedMessage
                defaultMessage="More goals"
                description="Pagination: load additional past or future goals"
              />
            </Typography>
          </Button>
        </div>
      )}
    </>
  );
}

export { HiringGoalDetailsList };
